<template>
  <div>
<!--    <h3>对话详情</h3>-->

    <div class="chat-container" ref="chatContainer">
      <!-- 显示加载状态 -->
      <div v-if="loading" style="text-align: center; padding: 20px;">
        <a-spin tip="加载中..." />
      </div>

      <!-- 显示无数据状态 -->
      <div v-else-if="qaPairs.length === 0" style="text-align: center; color: #999; padding: 20px;">
        暂无对话内容
      </div>

      <!-- 显示对话内容 -->
      <div v-else>
        <div
          v-for="(qaPair, index) in qaPairs"
          :key="qaPair.id || index"
          class="chat-message-wrapper"
        >
          <!-- 用户提问 - 右对齐 -->
          <div class="message-container user-container">
            <div class="message user-message">
              <div class="message-header">
                <span class="sender-name">用户</span>
                <span class="message-time">{{ formatDate(qaPair.created_at) }}</span>
              </div>
              <div class="message-content">{{ qaPair.query }}</div>
            </div>
            <div class="avatar user-avatar">👤</div>
          </div>

          <!-- AI 回答 - 左对齐 -->
          <div class="message-container assistant-container">
            <div class="avatar assistant-avatar">🤖</div>
            <div class="message assistant-message">
              <div class="message-header">
                <span class="sender-name">AI助手</span>
                <span class="message-time">{{ formatDate(qaPair.created_at) }}</span>
              </div>
              <div class="message-content" v-html="processedAnswer(qaPair.answer)"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="hasMore && !loading" style="text-align:center;margin-top:20px;">
      <a-button @click="loadMore" :loading="loading">加载更多</a-button>
    </div>
  </div>
</template>

<script>import marked from 'marked';
import DOMPurify from 'dompurify';

export default {
  name: 'ChatDetailModal',
  props: {
    conversationId: {
      type: String,
      default: '',
      required: false
    },
    user: {
      type: String,
      default: '',
      required: false
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      messages: [], // 原始消息数据
      loading: false,
      hasMore: false,
      firstId: null,
      limit: 20
    };
  },
  computed: {
    // 将原始数据转换为问答对格式
    qaPairs() {
      return this.messages.map((msg, index) => ({
        id: msg.id || `msg_${index}`,
        query: msg.query || '',
        answer: msg.answer || '',
        created_at: msg.created_at || msg.createdAt || msg.createTime
      }));
    }
  },
  watch: {
    visible: {
      handler(newVal) {
        if (newVal && this.conversationId) {
          this.resetData();
          this.$nextTick(() => {
            this.fetchMessages();
          });
        } else if (!newVal) {
          this.resetData();
        }
      },
      immediate: true
    },
    conversationId: {
      handler(newVal) {
        if (newVal && this.visible) {
          this.resetData();
          this.$nextTick(() => {
            this.fetchMessages();
          });
        }
      },
      immediate: true
    }
  },
  methods: {
    resetData() {
      this.messages = [];
      this.hasMore = false;
      this.firstId = null;
      this.loading = false;
    },

    async fetchMessages() {
      if (!this.conversationId || !this.visible) {
        return;
      }

      // 避免重复请求
      if (this.loading) {
        return;
      }

      this.loading = true;
      try {
        const res = await this.$http.get('/hhemp/ai/chat-messages', {
          params: {
            conversationId: this.conversationId,
            user: this.user,
            firstId: this.firstId,
            limit: this.limit
          }
        });

        const result = res.result;
        const newMessages = result.data || [];

        if (this.firstId) {
          // 向前翻页，追加到开头
          this.messages.unshift(...newMessages);
        } else {
          // 首次加载
          this.messages = newMessages;
        }

        this.hasMore = result.has_more || false;

        // 更新 firstId 为第一条记录的 ID（用于向前翻页）
        if (newMessages.length > 0 && !this.firstId) {
          this.firstId = newMessages[0].id;
        }

        this.$nextTick(() => {
          this.scrollToBottom();
        });

      } catch (err) {
        console.error('Fetch messages error:', err);
        this.$message.error('加载消息失败: ' + (err.message || '未知错误'));
      } finally {
        this.loading = false;
      }
    },

    loadMore() {
      this.fetchMessages();
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.chatContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },

    // 处理答案内容，包括 markdown 解析
    processedAnswer(answer) {
      if (!answer) return '';

      try {
        // 先清理原始内容
        let cleanAnswer = answer.trim();

        // 配置 marked 选项
        marked.setOptions({
          breaks: true,        // 转换 \n 为 <br>
          gfm: true,           // GitHub Flavored Markdown
          smartLists: true,
          smartypants: true,
          pedantic: false,
          xhtml: false
        });

        // 使用 marked 解析 markdown
        let html = marked.parse(cleanAnswer);

        // 使用 DOMPurify 清理 HTML 防止 XSS
        const cleanHtml = DOMPurify.sanitize(html, {
          ADD_TAGS: ['iframe'], // 如果需要iframe
          ADD_ATTR: ['target', 'rel'], // 如果需要链接属性
          FORBID_TAGS: ['script', 'style'],
          FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover']
        });

        //console.log('Processed markdown:', cleanAnswer, '->', cleanHtml);
        return cleanHtml;
      } catch (error) {
        console.error('Markdown processing error:', error);
        // 如果解析失败，返回转义后的原始文本
        return this.escapeHtml(answer);
      }
    },

    escapeHtml(text) {
      if (!text) return '';
      const map = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
      };
      return text.replace(/[&<>"']/g, function(m) { return map[m]; });
    },

    formatDate(timestamp) {
      if (!timestamp) return '';
      try {
        const date = new Date(timestamp * 1000);
        return date.toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        });
      } catch (error) {
        console.error('Date formatting error:', error);
        return '';
      }
    }
  }
};
</script>

<style scoped>
/* 调整对话容器样式 */
.chat-container {
  max-height: 60vh;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 20px;
  background-color: #ffffff;
}

.chat-message-wrapper {
  margin-bottom: 20px; /* 增加间距使布局更清晰 */
}

.message-container {
  display: flex;
  margin-bottom: 12px; /* 适当间距 */
  align-items: flex-start;
}

/* 用户提问 - 右对齐 */
.user-container {
  justify-content: flex-end;
}

.user-container .message {
  background-color: #e3f2fd;
  border: 1px solid #bbdefb;
  border-radius: 18px 4px 18px 18px;
}

.user-container .avatar {
  margin-left: 12px;
  margin-right: 0;
}

/* AI 回答 - 左对齐 */
.assistant-container {
  justify-content: flex-start;
}

.assistant-container .message {
  background-color: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 4px 18px 18px 18px;
}

.avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
  margin-right: 12px;
}

.user-avatar {
  background-color: #bbdefb;
  color: #0d47a1;
}

.assistant-avatar {
  background-color: #e0e0e0;
  color: #424242;
}

.message {
  max-width: 80%;
  padding: 12px 16px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

.message-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  padding-bottom: 6px;
}

.sender-name {
  font-weight: 600;
  font-size: 14px; /* 增大字体 */
  color: #333;
}

.message-time {
  font-size: 12px; /* 增大字体 */
  color: #888;
}

/* 关键：调整消息内容样式 - 解决行间距问题 */
.message-content {
  line-height: 1.6; /* 适中的行高 */
  font-size: 15px; /* 增大字体 */
  word-wrap: break-word;
  color: #333;
  white-space: normal; /* 关键修改：改为normal，让浏览器自动处理换行 */
  overflow-wrap: break-word;
}

/* Markdown 样式 - 适配增大字体 */
.message-content ::v-deep p {
  margin: 0 0 10px 0; /* 适中的段落间距 */
  line-height: 1.6;
  word-wrap: break-word;
}

.message-content ::v-deep ul,
.message-content ::v-deep ol {
  margin: 10px 0;
  padding-left: 24px;
}

.message-content ::v-deep li {
  margin: 5px 0;
  line-height: 1.6;
}

.message-content ::v-deep pre {
  background-color: #f8f8f8;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 12px;
  overflow-x: auto;
  margin: 12px 0;
  font-size: 14px; /* 增大代码字体 */
  line-height: 1.5;
  /* 代码块内保留原始格式 */
  white-space: pre;
}

.message-content ::v-deep code {
  background-color: #f0f0f0;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Consolas', 'Courier New', monospace;
  font-size: 14px; /* 增大代码字体 */
  color: #d63384;
  word-break: break-word;
}

.message-content ::v-deep pre code {
  background-color: transparent;
  color: inherit;
  padding: 0;
  word-break: normal;
}

.message-content ::v-deep strong {
  font-weight: 600;
  color: #333;
}

.message-content ::v-deep em {
  font-style: italic;
}

.message-content ::v-deep blockquote {
  border-left: 4px solid #1976d2;
  margin: 12px 0;
  padding: 8px 16px;
  background-color: #f5f9ff;
  color: #555;
}

.message-content ::v-deep h1,
.message-content ::v-deep h2,
.message-content ::v-deep h3,
.message-content ::v-deep h4,
.message-content ::v-deep h5,
.message-content ::v-deep h6 {
  margin: 16px 0 12px 0;
  font-weight: 600;
  color: #333;
  line-height: 1.3;
}

.message-content ::v-deep h1 {
  font-size: 24px; /* 增大标题字体 */
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.message-content ::v-deep h2 {
  font-size: 22px; /* 增大标题字体 */
}

.message-content ::v-deep h3 {
  font-size: 20px; /* 增大标题字体 */
}

.message-content ::v-deep a {
  color: #1976d2;
  text-decoration: none;
  border-bottom: 1px dotted #1976d2;
}

.message-content ::v-deep a:hover {
  text-decoration: underline;
  border-bottom: 1px solid #1976d2;
}

.message-content ::v-deep hr {
  border: 0;
  border-top: 1px solid #e0e0e0;
  margin: 16px 0;
}

.message-content ::v-deep table {
  border-collapse: collapse;
  width: 100%;
  margin: 12px 0;
  display: block;
  overflow-x: auto;
}

.message-content ::v-deep th,
.message-content ::v-deep td {
  border: 1px solid #e0e0e0;
  padding: 8px 12px;
  text-align: left;
}

.message-content ::v-deep th {
  background-color: #f5f5f5;
  font-weight: 600;
}

/* 确保列表项正确显示 */
.message-content ::v-deep ul li::before {
  content: "•";
  color: #666;
  display: inline-block;
  width: 1em;
  margin-left: -1em;
}

.message-content ::v-deep ol li::before {
  color: #666;
  display: inline-block;
  width: 1em;
  margin-left: -1em;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .message {
    max-width: 85%;
  }

  .avatar {
    width: 32px;
    height: 32px;
    font-size: 14px;
    margin-right: 8px;
  }

  .message-content {
    font-size: 14px; /* 移动端适配字体 */
  }

  .sender-name {
    font-size: 13px;
  }

  .message-time {
    font-size: 11px;
  }

  .chat-message-wrapper {
    margin-bottom: 16px;
  }
}
</style>